import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../apiClient';

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
// A change event that follows this client's own save by less than this is the echo of that
// save, so the page does not reload a second time.
const OWN_SAVE_ECHO_MS = 1500;

let lastOwnSaveAt = 0;

/** Call after this client saves successfully, so its own change event is not treated as news. */
export function markOwnSave(): void {
  lastOwnSaveAt = Date.now();
}

/** Reconnect delay: 1 s doubling to a 30 s cap, plus up to 25% jitter. */
export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
  return Math.round(base + base * 0.25 * random());
}

/**
 * Calls onChange whenever the Horizontal dashboard data changes on the server.
 *
 * The server only says "something changed"; the page then reloads its own data. The stream
 * carries no Jira data or credentials.
 *
 * - Changes that arrive within a moment of each other are coalesced into one call.
 * - A change that is the echo of this client's own save (see markOwnSave) is ignored.
 * - On a dropped connection it reconnects with exponential backoff and jitter, waits while the
 *   tab is hidden, and calls onChange once after reconnecting to catch up on missed changes.
 */
export function useDashboardChanges(onChange: () => void): void {
  const handler = useRef(onChange);
  handler.current = onChange;

  useEffect(() => {
    if (typeof EventSource === 'undefined') return undefined;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    let attempt = 0;
    let everConnected = false;
    let needsCatchUp = false;

    const fire = () => {
      debounce = undefined;
      handler.current();
    };
    const scheduleReload = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(fire, 500);
    };

    const open = () => {
      retry = undefined;
      source = new EventSource(`${API_BASE_URL}/api/horizontal-actions/events`);
      source.onopen = () => {
        attempt = 0;
        if (everConnected && needsCatchUp) scheduleReload();
        everConnected = true;
        needsCatchUp = false;
      };
      source.addEventListener('changed', () => {
        if (Date.now() - lastOwnSaveAt < OWN_SAVE_ECHO_MS) return;
        scheduleReload();
      });
      source.onerror = () => {
        source?.close();
        needsCatchUp = true;
        if (closed) return;
        const wait = reconnectDelay(attempt);
        attempt += 1;
        retry = setTimeout(openWhenVisible, wait);
      };
    };

    // Do not reconnect in a background tab; resume when it becomes visible again.
    const openWhenVisible = () => {
      if (closed) return;
      if (typeof document !== 'undefined' && document.hidden) {
        retry = setTimeout(openWhenVisible, 1000);
        return;
      }
      open();
    };

    open();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (debounce) clearTimeout(debounce);
      source?.close();
    };
  }, []);
}
